import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Mic, MicOff, Video, VideoOff, PhoneOff, 
  Volume2, VolumeX, Phone, ChevronDown, ChevronRight,
  MessageSquare, FileText, IndianRupee
} from 'lucide-react';
import InCallChat from '@/components/InCallChat';
import { useAuth } from '@/hooks/use-auth';
import { useSocketReceiver, useSocketEmitter, useSockets } from '@/hooks/use-sockets';
import AgoraRTC from 'agora-rtc-react';
import { getGeneratedAgoraToken } from '@/api/token';
import { client as apiClient } from '@/api';

const AGORA_APP_ID = import.meta.env.VITE_AGORA_APP_ID;
const HEARTBEAT_INTERVAL_MS = 10000;
const SESSION_WARNING_SECONDS = 25 * 60;
const SESSION_LIMIT_SECONDS = 30 * 60;

// How long we hold the call open after Agora reports the remote user dropped
// (reason='ServerTimeOut'). Matches the server's total tolerance:
//   heartbeatMissedThresholdSeconds (30) + disconnectGraceSeconds (30) = 60s.
// If the user reconnects within this window, Agora fires `user-joined` and
// we dismiss the overlay seamlessly. If not, we cleanup with attribution to
// the missing side. Voluntary hangups (reason='Quit') skip this entirely —
// there's no one to wait for.
const RECONNECT_WINDOW_SECONDS = 60;

// Round a rupee amount for display. The billing engine produces values like
// 29.700000000000003 (JS float multiplication: 99 × 0.30); rounding to 2dp
// keeps currency UI clean. Trailing ".00" is stripped so whole amounts show
// as "99" rather than "99.00".
const fmtMoney = (n) => {
  const v = Number(n) || 0;
  const fixed = v.toFixed(2);
  return fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed;
};

// Loading screen component
const LoadingScreen = ({ userName, mode }) => (
  <div className="flex flex-col h-screen bg-[#FFF8F0] items-center justify-center">
    <div className="text-center">
      <div className="relative w-28 h-28 mx-auto mb-6">
        <div className="absolute inset-0 rounded-full border-4 border-orange-200 animate-ping opacity-30" />
        <div className="absolute inset-3 rounded-full border-4 border-orange-300 animate-pulse" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
            <Phone className="w-8 h-8 text-white" />
          </div>
        </div>
      </div>
      
      <h2 className="text-xl font-semibold text-gray-800 mb-2">
        Connecting to {userName || 'User'}
      </h2>
      <p className="text-gray-500 text-sm">
        Setting up your {mode === 'video' ? 'video' : 'voice'} call...
      </p>
      
      <div className="flex justify-center gap-1.5 mt-5">
        <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

// Session timer component
const SessionTimer = ({ seconds, isConnected }) => {
  const isWarning = seconds >= SESSION_WARNING_SECONDS && seconds < SESSION_LIMIT_SECONDS;
  const isOvertime = seconds >= SESSION_LIMIT_SECONDS;
  
  const formatTime = (secs) => {
    const hours = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const remainingSecs = secs % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`;
  };
  
  if (!isConnected) {
    return <div className="text-gray-400 font-mono text-lg">--:--</div>;
  }
  
  return (
    <div className="text-center">
      <div className={`font-mono text-xl font-bold ${
        isOvertime ? 'text-red-600 animate-pulse' : 
        isWarning ? 'text-red-500 animate-pulse' : 
        'text-gray-800'
      }`}>
        {formatTime(seconds)}
      </div>
      {isWarning && !isOvertime && (
        <div className="text-xs text-red-500 font-semibold mt-0.5">Session ending soon</div>
      )}
      {isOvertime && (
        <div className="text-xs text-red-600 font-semibold mt-0.5">Session time exceeded</div>
      )}
    </div>
  );
};

// User avatar component
const UserAvatar = ({ name, imageUrl, size = 'lg' }) => {
  const sizeClasses = size === 'lg' ? 'w-36 h-36' : 'w-24 h-24';
  const textSize = size === 'lg' ? 'text-4xl' : 'text-xl';
  
  return (
    <div className={`${sizeClasses} bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center overflow-hidden shadow-md`}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={name}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ) : (
        <span className={`${textSize} font-bold text-orange-500`}>
          {name?.charAt(0)?.toUpperCase() || 'U'}
        </span>
      )}
    </div>
  );
};

// Sidebar panel component
const SidebarPanel = ({ title, isExpanded, onToggle, children }) => (
  <div className="border-b border-gray-200">
    <button
      onClick={onToggle}
      className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
    >
      <h3 className="font-semibold text-gray-800">{title}</h3>
      <span className="text-orange-500">
        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </span>
    </button>
    {isExpanded && (
      <div className="px-4 pb-4 bg-gray-50">
        {children}
      </div>
    )}
  </div>
);

/**
 * Unified Call Page for Partner App
 * Matches as-webapp design system with cream backgrounds, orange primary, and professional layout
 */
const CallPage = () => {
  console.log('[CallPage] NEW UNIFIED CALL PAGE LOADED - v2');
  const { channel } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { astrologer } = useAuth();
  const astrologerRate = astrologer?.personalInformation?.rate || 0;

  const { userSocketId, userId, userName, userPicture, mode: passedMode } = location.state || {};
  
  // Determine mode: from state, or infer from URL path for backward compatibility
  const inferModeFromPath = () => {
    if (location.pathname.includes('/video/')) return 'video';
    if (location.pathname.includes('/voice/')) return 'voice';
    return 'voice';
  };
  const mode = passedMode || inferModeFromPath();
  
  // Initialization states
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState(null);
  
  // Connection states
  const [connectionState, setConnectionState] = useState('disconnected');
  const [remoteUsers, setRemoteUsers] = useState([]);
  
  // Media states
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(mode === 'video');
  const [speakerEnabled, setSpeakerEnabled] = useState(true);
  
  // UI states
  const [callDuration, setCallDuration] = useState(0);
  const [showConfirmEnd, setShowConfirmEnd] = useState(false);

  // Live earnings polled from billing status — what partner actually earns
  // for this specific session (net of commission, excluding any reversals).
  // `liveBilling` null until first poll succeeds.
  const [liveBilling, setLiveBilling] = useState(null);
  // Ref-mirror so cleanup() can read the latest poll snapshot via a stable
  // closure. Putting liveBilling in cleanup's useCallback deps re-creates
  // cleanup every 5s, but Agora listeners are registered once with []
  // deps — they'd capture render-1's cleanup and read render-1's
  // (initial-null) liveBilling. The ref pattern dodges that entirely.
  const liveBillingRef = useRef(null);
  // Tracks whether the local Agora connection has been in RECONNECTING
  // state since the last successful CONNECTED. Used to decide whether to
  // run a post-reconnect session-status check — without it, a clean
  // initial CONNECTED would also trigger the check unnecessarily. We also
  // need this on the next CONNECTED transition to detect "session was
  // force-ended on the server while we were offline" cases that no other
  // path catches (Agora keeps RECONNECTING for minutes; socket pushes are
  // missed during the offline window; the partner-app didn't end on its
  // own).
  const wasReconnectingRef = useRef(false);

  // Post-call summary — when set, we replace the call UI with a summary
  // card showing what the partner just earned. User's side has called
  // endBilling by then so numbers include the final cycle.
  const [callSummary, setCallSummary] = useState(null);
  
  // Sidebar states (desktop)
  const [userInfoExpanded, setUserInfoExpanded] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);

  // User details submitted during call waiting
  const [userDetails, setUserDetails] = useState(null);

  // Reconnect overlay state — shown when Agora reports the user dropped
  // (reason='ServerTimeOut') so the astrologer doesn't immediately see the
  // call die. The countdown ticks `reconnectSecondsLeft` down each second
  // for visible feedback. Both UI render state and a ref-mirror are needed:
  // the Agora user-joined listener is a closure captured at registration
  // time, so it can't read live state — it reads `isWaitingForReconnectRef`.
  const [isWaitingForReconnect, setIsWaitingForReconnect] = useState(false);
  const [reconnectSecondsLeft, setReconnectSecondsLeft] = useState(0);

  // Local network indicator — shown when this browser's OWN Agora connection
  // is in 'RECONNECTING' state, so the astrologer knows their network is
  // unstable. Distinct from `isWaitingForReconnect` (which is for the user's
  // network drop). Cleared when Agora returns to 'CONNECTED'. We deliberately
  // do NOT auto-cleanup on DISCONNECTED here — partner-app didn't have that
  // behavior before this change, and adding it would be out of scope.
  const [isLocalReconnecting, setIsLocalReconnecting] = useState(false);

  // Refs
  const clientRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const isInitializedRef = useRef(false);
  const uid = useRef(Math.floor(Math.random() * 10000000));
  const heartbeatRef = useRef(null);
  const hasCleanedUp = useRef(false);
  const callDurationRef = useRef(0);
  // Forward-reference ref so the socket effect (defined above cleanup)
  // can call cleanup without a TDZ error. Set in an effect after cleanup
  // is declared (see below).
  const cleanupRef = useRef(null);
  // Reconnect-window bookkeeping. Refs (not state) because the Agora
  // listeners' closures need live access — state reads inside listeners
  // are stale by registration time.
  const reconnectTimerRef = useRef(null);          // setTimeout for the 60s expiry
  const reconnectIntervalRef = useRef(null);       // setInterval for the 1s countdown
  const isWaitingForReconnectRef = useRef(false);  // mirror of the state flag

  // Real-time push from server when billing session ends. Faster than the
  // 5s billing-status poll: audio cuts within ~3s of server-side end
  // (Agora teardown only) instead of 10-15s. Poll stays as fallback.
  const { socket } = useSockets();
  useEffect(() => {
    if (!socket || connectionState !== 'connected') return;

    socket.emit('billing:join-room', { channelName: channel });

    const handler = (data) => {
      if (data?.channelName !== channel) return;
      if (hasCleanedUp.current) return;
      console.log(`[CallPage] socket session-ended received reason=${data?.endReason}`);
      cleanupRef.current?.(data?.endReason || 'remote_user_left');
    };
    socket.on('billing:session-ended', handler);

    return () => {
      socket.off('billing:session-ended', handler);
      socket.emit('billing:leave-room', { channelName: channel });
    };
  }, [socket, connectionState, channel]);

  // Listen for user details submitted during call (real-time path for late submissions)
  useSocketReceiver('call-user-details', (data) => {
    if (data?.channelName === channel && data?.userDetails) {
      console.log('[CallPage] Received user details via socket');
      setUserDetails(data.userDetails);
      setUserInfoExpanded(true);
    }
  });

  // Fetch call details from DB on mount (primary path — catches details submitted before CallPage mounted)
  useEffect(() => {
    if (!channel) return;
    let cancelled = false;

    const fetchCallDetails = async () => {
      try {
        console.log('[CallPage] Fetching call details for channel:', channel);
        const res = await apiClient.get(`/api/calls/channel/${channel}`);
        const call = res.data?.data;
        console.log('[CallPage] Fetched call, userDetails:', call?.userDetails);
        if (!cancelled && call?.userDetails) {
          const d = call.userDetails;
          // Only set if there's actual data (not all empty)
          if (d.dateOfBirth || d.timeOfBirth || d.placeOfBirth || d.concern) {
            setUserDetails(d);
            setUserInfoExpanded(true);
          }
        }
      } catch (err) {
        console.error('[CallPage] Failed to fetch call details:', err);
      }
    };

    // Fetch immediately
    fetchCallDetails();
    // Retry once after 5s to catch details submitted during the accept→mount transition
    const retryTimeout = setTimeout(fetchCallDetails, 5000);

    return () => {
      cancelled = true;
      clearTimeout(retryTimeout);
    };
  }, [channel]);

  // Update call status in database
  const updateCallStatus = useCallback(async (status, endReason) => {
    try {
      await apiClient.patch(`/api/calls/channel/${channel}/status`, {
        status,
        endReason,
      });
      console.log(`[CallPage] Call status updated to: ${status}`);
    } catch (error) {
      console.error('[CallPage] Failed to update call status:', error);
    }
  }, [channel]);

  // Cleanup function
  const cleanup = useCallback(async (reason = 'astrologer_ended') => {
    if (hasCleanedUp.current) return;
    hasCleanedUp.current = true;

    console.log('[CallPage] Cleaning up, reason:', reason);

    // Tear down reconnect overlay state if it's still up. Idempotent — if no
    // reconnect was in progress these are already null. Done first so the
    // overlay disappears synchronously even if the cleanup later awaits.
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (reconnectIntervalRef.current) {
      clearInterval(reconnectIntervalRef.current);
      reconnectIntervalRef.current = null;
    }
    isWaitingForReconnectRef.current = false;
    setIsWaitingForReconnect(false);
    setReconnectSecondsLeft(0);

    // Stop heartbeat
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    
    try {
      // Unpublish tracks first
      const tracksToUnpublish = [];
      if (localAudioTrackRef.current) tracksToUnpublish.push(localAudioTrackRef.current);
      if (localVideoTrackRef.current) tracksToUnpublish.push(localVideoTrackRef.current);
      
      if (tracksToUnpublish.length > 0 && clientRef.current) {
        await clientRef.current.unpublish(tracksToUnpublish);
        console.log('[CallPage] Unpublished tracks');
      }
    } catch (e) {
      console.error('[CallPage] Error unpublishing tracks:', e);
    }
    
    try {
      // Stop and close tracks with Safari-specific handling
      if (localAudioTrackRef.current) {
        const mediaStreamTrack = localAudioTrackRef.current.getMediaStreamTrack?.();
        if (mediaStreamTrack) mediaStreamTrack.stop();
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
        localAudioTrackRef.current = null;
      }
      
      if (localVideoTrackRef.current) {
        const mediaStreamTrack = localVideoTrackRef.current.getMediaStreamTrack?.();
        if (mediaStreamTrack) mediaStreamTrack.stop();
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
        localVideoTrackRef.current = null;
      }
      
      // Leave channel
      if (clientRef.current) {
        await clientRef.current.leave();
        clientRef.current = null;
      }
      
      // Update call status
      await updateCallStatus('completed', reason);

      // Reset state
      setRemoteUsers([]);
      setConnectionState('disconnected');

    } catch (error) {
      console.error('[CallPage] Cleanup error:', error);
    }

    // Fetch final billing state for the post-call summary.
    // We don't call endBilling from here — the user-side CallClient handles
    // that (it owns attribution). We just poll once to pick up the final
    // numbers. If the user-side hasn't ended billing yet, we'll still show
    // whatever cycles have been written so far.
    //
    // Retry the fetch a couple of times — the astrologer's network may still
    // be flaky right after a drop+recover, and a single failed fetch used to
    // fall straight through to a misleading "₹0 earnings" summary.
    let finalData = null;
    const maxAttempts = 3;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // First attempt waits 2.5s to give user-side's endBilling time to
        // land + write its final cycle. Subsequent attempts use a shorter
        // delay so total wait stays bounded (~5.5s in the worst case).
        await new Promise(r => setTimeout(r, attempt === 0 ? 2500 : 1500));
        const res = await apiClient.get(`/api/calls/channel/${channel}/billing/status`);
        const data = res.data?.data;
        if (data) {
          finalData = data;
          break;
        }
      } catch (err) {
        console.debug(`[CallPage] Billing summary fetch attempt ${attempt + 1} failed:`, err);
      }
    }

    if (finalData) {
      setCallSummary({
        ...finalData,
        durationSeconds: callDurationRef.current,
        reason,
        userName: userName,
      });
      return;
    }

    // Fallback to the most recent in-call poll snapshot. `liveBillingRef`
    // is refreshed every 5s during the call, so its value reflects what was
    // billed up to ~5s before the drop. The final cycle (if any) might be
    // missing, but this is FAR better than showing zeros — the astrologer
    // earned this money and seeing ₹0 misleads them into thinking they
    // didn't. Their actual earnings ledger row in the DB is intact either
    // way; this is purely about what we render.
    const liveBillingSnapshot = liveBillingRef.current;
    if (liveBillingSnapshot) {
      setCallSummary({
        ...liveBillingSnapshot,
        durationSeconds: callDurationRef.current,
        reason,
        userName,
      });
      return;
    }

    // Last-resort fallback — only hit if the in-call poll never populated
    // (call ended before the first 5s tick). For very-short calls there's
    // effectively nothing to show anyway.
    setCallSummary({
      durationSeconds: callDurationRef.current,
      reason,
      userName,
      partnerGrossEarning: 0,
      partnerCommissionAmount: 0,
      partnerNetEarning: 0,
    });
  }, [updateCallStatus, channel, userName]);

  // Keep cleanupRef pointed at the latest cleanup so the socket effect
  // (declared above cleanup in the file) can call it without a TDZ error.
  useEffect(() => {
    cleanupRef.current = cleanup;
  }, [cleanup]);

  // Initialize Agora
  useEffect(() => {
    const initializeCall = async () => {
      if (isInitializedRef.current || !channel || !AGORA_APP_ID) return;
      
      isInitializedRef.current = true;
      console.log('[CallPage] Initializing Agora for channel:', channel);
      
      try {
        setConnectionState('connecting');
        
        // Create client
        clientRef.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        
        // Set up event listeners
        clientRef.current.on('user-joined', (user) => {
          console.log('[CallPage] User joined:', user.uid);
          setRemoteUsers(prev => [...prev.filter(u => u.uid !== user.uid), user]);

          // If we were waiting for the user to reconnect, they're back —
          // dismiss the overlay and let the call resume normally. Read from
          // the ref because closures capture stale state at registration
          // time. Idempotent: dismissing when not waiting is a no-op.
          if (isWaitingForReconnectRef.current) {
            console.log('[CallPage] User rejoined — dismissing reconnect overlay');
            if (reconnectTimerRef.current) {
              clearTimeout(reconnectTimerRef.current);
              reconnectTimerRef.current = null;
            }
            if (reconnectIntervalRef.current) {
              clearInterval(reconnectIntervalRef.current);
              reconnectIntervalRef.current = null;
            }
            isWaitingForReconnectRef.current = false;
            setIsWaitingForReconnect(false);
            setReconnectSecondsLeft(0);
          }
        });

        clientRef.current.on('user-left', (user, reason) => {
          console.log('[CallPage] User left:', user.uid, 'reason:', reason);
          setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));

          // NOTE: don't gate on connectionState here — that value is captured
          // in this listener's closure from when it was registered (while
          // connectionState was still 'connecting'), so the check is always
          // stale. hasCleanedUp.current already prevents duplicate cleanup.
          // We also no longer navigate() here — cleanup populates callSummary
          // which renders the post-call earnings screen.
          //
          // Branch on Agora's `reason`:
          //   'Quit'          → user voluntarily called client.leave(). End
          //                     the call. No reconnect window — no one to
          //                     wait for.
          //   'ServerTimeOut' → user's network died. Show reconnect overlay
          //                     and wait up to RECONNECT_WINDOW_SECONDS for
          //                     `user-joined` to fire (Agora redelivers it
          //                     when the user's client rejoins the channel
          //                     with the same UID). If the timer expires
          //                     first, cleanup with reason='user_disconnected'.
          //   anything else   → preserve the old 2s-cleanup behavior so we
          //                     don't accidentally hold calls open for some
          //                     unanticipated reason.
          if (reason === 'ServerTimeOut') {
            // Already waiting (e.g. user dropped, came back briefly, dropped
            // again before user-joined cleared the flag) — don't double-arm.
            if (isWaitingForReconnectRef.current || hasCleanedUp.current) return;

            console.log(`[CallPage] User network drop — holding call for ${RECONNECT_WINDOW_SECONDS}s`);
            isWaitingForReconnectRef.current = true;
            setIsWaitingForReconnect(true);
            setReconnectSecondsLeft(RECONNECT_WINDOW_SECONDS);

            // 1s countdown for the visible "Holding call for Ns…" display.
            reconnectIntervalRef.current = setInterval(() => {
              setReconnectSecondsLeft(prev => Math.max(0, prev - 1));
            }, 1000);

            // Hard timeout — if user-joined doesn't fire within the window,
            // give up and cleanup. `user_disconnected` is a valid Call.endReason
            // enum value and accurately describes the situation.
            reconnectTimerRef.current = setTimeout(() => {
              if (!hasCleanedUp.current) {
                console.log('[CallPage] Reconnect window expired — cleaning up');
                cleanup('user_disconnected');
              }
            }, RECONNECT_WINDOW_SECONDS * 1000);
          } else {
            // 'Quit' (voluntary) or unknown reason — existing fast-cleanup path.
            setTimeout(() => {
              setRemoteUsers(current => {
                if (current.length === 0 && !hasCleanedUp.current) {
                  console.log('[CallPage] Remote user disconnected, ending call');
                  cleanup('remote_user_left');
                }
                return current;
              });
            }, 2000);
          }
        });
        
        clientRef.current.on('user-published', async (user, mediaType) => {
          console.log('[CallPage] User published:', user.uid, mediaType);
          
          try {
            await clientRef.current.subscribe(user, mediaType);
            
            if (mediaType === 'audio' && user.audioTrack) {
              user.audioTrack.setVolume(speakerEnabled ? 100 : 0);
              user.audioTrack.play();
            }
            
            if (mediaType === 'video' && user.videoTrack) {
              setRemoteUsers(prev => 
                prev.map(u => u.uid === user.uid ? { ...u, videoTrack: user.videoTrack } : u)
              );
              
              // Play video after state update
              setTimeout(() => {
                const videoEl = remoteVideoRefs.current[user.uid];
                if (videoEl && user.videoTrack) {
                  user.videoTrack.play(videoEl);
                }
              }, 100);
            }
          } catch (error) {
            console.error('[CallPage] Failed to subscribe:', error);
          }
        });
        
        clientRef.current.on('user-unpublished', (user, mediaType) => {
          console.log('[CallPage] User unpublished:', user.uid, mediaType);
          if (mediaType === 'video') {
            setRemoteUsers(prev =>
              prev.map(u => u.uid === user.uid ? { ...u, videoTrack: null } : u)
            );
          }
        });

        // Local network indicator + self-cleanup paths. Tracks Agora's local
        // connection state for THIS browser (the astrologer's own connection).
        //
        // Three branches:
        //   RECONNECTING — astrologer's wifi is flaky / went offline. Show
        //     yellow "Reconnecting…" pill. Mark wasReconnectingRef so the
        //     next CONNECTED transition triggers a server-state check.
        //   CONNECTED   — wifi is back. Hide the pill. If we were in
        //     RECONNECTING just before this, the server may have force-ended
        //     the session while we were offline (pause-grace expired, refund
        //     fired, billing:session-ended socket push was missed because
        //     our socket was offline too). Query billing status — if
        //     active=false, run cleanup so the UI transitions to the post-
        //     call summary instead of sitting on a stale "Connected" state.
        //   DISCONNECTED — Agora gave up after exhausting internal reconnect
        //     attempts. Mirror the user-side behavior (which has had this
        //     branch from day one): cleanup so the astrologer sees the
        //     summary instead of a dead call screen. Cleanup is idempotent
        //     via hasCleanedUp.current — safe even if other paths also fire.
        clientRef.current.on('connection-state-change', async (curState) => {
          console.log('[CallPage] connection-state-change:', curState);
          if (curState === 'RECONNECTING') {
            setIsLocalReconnecting(true);
            wasReconnectingRef.current = true;
          } else if (curState === 'CONNECTED') {
            setIsLocalReconnecting(false);

            // Post-reconnect session-state check. Only fires if we just
            // recovered from RECONNECTING (initial CONNECTED skips it).
            if (wasReconnectingRef.current && !hasCleanedUp.current) {
              wasReconnectingRef.current = false;
              try {
                const res = await apiClient.get(`/api/calls/channel/${channel}/billing/status`);
                const data = res.data?.data;
                // Cleanup ONLY when the session explicitly transitioned to
                // ENDED or ORPHANED. We can't use `data.active === false`
                // alone because the same shape is returned when the session
                // simply doesn't exist yet (controller's no-session branch
                // returns `{ active: false, message: "..." }` with no
                // status field). That bit us before — the partner-app
                // sometimes polled before the user-side called startBilling
                // and treated "doesn't exist yet" as "ended", killing the
                // call instantly. Checking `status === "ENDED"` (or
                // ORPHANED) means we only act when the server has authored
                // a real terminal state.
                const sessionEnded = data && (data.status === "ENDED" || data.status === "ORPHANED");
                if (sessionEnded && !hasCleanedUp.current) {
                  console.log(`[CallPage] Server reports session ${data.status} (reason=${data.endReason}) — cleaning up`);
                  cleanup(data.endReason || 'network_timeout');
                }
              } catch (err) {
                console.debug('[CallPage] Post-reconnect session check failed:', err);
              }
            }
          } else if (curState === 'DISCONNECTED' && !hasCleanedUp.current) {
            console.log('[CallPage] Local Agora DISCONNECTED — cleaning up');
            cleanup('connection_lost');
          }
        });

        // Get token
        const token = await getGeneratedAgoraToken(channel, uid.current);
        if (!token) {
          throw new Error('Failed to get token');
        }
        
        // Join channel
        await clientRef.current.join(AGORA_APP_ID, channel, token, uid.current);
        console.log('[CallPage] Joined channel successfully');
        
        // Create audio track
        localAudioTrackRef.current = await AgoraRTC.createMicrophoneAudioTrack({
          encoderConfig: 'speech_standard'
        });
        
        // Create video track for video calls
        if (mode === 'video') {
          localVideoTrackRef.current = await AgoraRTC.createCameraVideoTrack();
          
          // Play local video
          if (localVideoRef.current && localVideoTrackRef.current) {
            localVideoTrackRef.current.play(localVideoRef.current);
          }
        }
        
        // Publish tracks
        const tracksToPublish = [localAudioTrackRef.current];
        if (localVideoTrackRef.current) {
          tracksToPublish.push(localVideoTrackRef.current);
        }
        await clientRef.current.publish(tracksToPublish);
        console.log('[CallPage] Published tracks');
        
        setIsInitializing(false);
        setConnectionState('connected');
        await updateCallStatus('active');
        
      } catch (error) {
        console.error('[CallPage] Failed to initialize:', error);
        setInitError(error.message || 'Failed to initialize call');
        setIsInitializing(false);
        setConnectionState('failed');
      }
    };
    
    initializeCall();
    
    return () => {
      if (!hasCleanedUp.current) {
        cleanup('component_unmount');
      }
    };
  }, []);

  // Heartbeat
  useEffect(() => {
    if (connectionState !== 'connected') return;

    const sendHeartbeat = async () => {
      try {
        await apiClient.post(`/api/calls/channel/${channel}/heartbeat`, {
          participant: 'astrologer',
          uid: uid.current,
        });
      } catch (error) {
        console.debug('[CallPage] Heartbeat failed:', error);
      }
    };

    sendHeartbeat();
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [connectionState, channel]);

  // Poll billing status every 5s to show live net earnings.
  // Billing cycles run once per minute on the server; a 5s poll means the
  // UI picks up each cycle within ~5s of it writing. Cheap enough for
  // launch scale — one tiny aggregation query per poll.
  useEffect(() => {
    if (connectionState !== 'connected') return;

    const fetchStatus = async () => {
      try {
        const res = await apiClient.get(`/api/calls/channel/${channel}/billing/status`);
        const data = res.data?.data;
        if (data?.active !== undefined) {
          setLiveBilling(data);
          // Mirror to ref so cleanup() can read it without taking liveBilling
          // as a dependency (which would invalidate cleanup's cached reference
          // every 5s and leave Agora listeners holding stale closures).
          liveBillingRef.current = data;
        }
      } catch (err) {
        console.debug('[CallPage] Billing status poll failed:', err);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [connectionState, channel]);

  // Timer
  useEffect(() => {
    let interval;
    if (connectionState === 'connected') {
      interval = setInterval(() => {
        setCallDuration(prev => {
          callDurationRef.current = prev + 1;
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [connectionState]);

  // Page unload handler
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (connectionState === 'connected' && !hasCleanedUp.current) {
        cleanup('tab_closed');
        e.preventDefault();
        e.returnValue = 'You are in a call. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [connectionState, cleanup]);

  // Toggle functions
  const toggleAudio = async () => {
    if (!localAudioTrackRef.current) return;
    try {
      const newState = !audioEnabled;
      await localAudioTrackRef.current.setEnabled(newState);
      setAudioEnabled(newState);
    } catch (error) {
      console.error('[CallPage] Failed to toggle audio:', error);
    }
  };

  const toggleVideo = async () => {
    if (!localVideoTrackRef.current) return;
    try {
      const newState = !videoEnabled;
      await localVideoTrackRef.current.setEnabled(newState);
      setVideoEnabled(newState);
    } catch (error) {
      console.error('[CallPage] Failed to toggle video:', error);
    }
  };

  const toggleSpeaker = () => {
    const newState = !speakerEnabled;
    setSpeakerEnabled(newState);
    
    remoteUsers.forEach(user => {
      if (user.audioTrack) {
        user.audioTrack.setVolume(newState ? 100 : 0);
      }
    });
  };

  const handleEndCall = async () => {
    await cleanup('astrologer_ended');
    // Don't navigate — cleanup sets callSummary which renders the post-call
    // earnings screen. Partner clicks "Done" there to return to dashboard.
  };

  const getFormattedDate = () => {
    return new Date().toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Post-call summary — shown after the call ends, before navigating back
  // to dashboard. Displays gross / commission / net earned for THIS session.
  if (callSummary) {
    const mins = Math.floor((callSummary.durationSeconds || 0) / 60);
    const secs = (callSummary.durationSeconds || 0) % 60;
    const durationStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

    return (
      <div className="flex flex-col h-screen bg-[#FFF8F0] items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <IndianRupee className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Call Ended</h2>
            <p className="text-gray-500 mt-1">with {callSummary.userName || 'User'}</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Duration</span>
              <span className="text-sm font-semibold text-gray-900">{durationStr}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Gross</span>
              <span>₹{fmtMoney(callSummary.partnerGrossEarning)}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Platform commission ({callSummary.commissionPercent ?? 30}%)</span>
              <span>− ₹{fmtMoney(callSummary.partnerCommissionAmount)}</span>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
              <span className="text-base font-semibold text-gray-800">Your Earning</span>
              <span className="text-2xl font-bold text-green-600">
                ₹{fmtMoney(callSummary.partnerNetEarning)}
              </span>
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center mb-5">
            Added to your earnings. View full history on the Earnings page.
          </p>

          <button
            onClick={() => navigate('/', { replace: true })}
            className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isInitializing) {
    return <LoadingScreen userName={userName} mode={mode} />;
  }

  // Error state
  if (initError) {
    return (
      <div className="flex flex-col h-screen bg-[#FFF8F0] items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center border border-gray-100">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PhoneOff className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Connection Failed</h2>
          <p className="text-gray-500 mb-6">{initError}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="text-center py-4 bg-white border-b border-gray-200">
        <h1 className="text-xl font-bold text-gray-900">User Consultation</h1>
        <p className="text-sm text-gray-500">{getFormattedDate()}</p>
        
        {/* Participant names and status */}
        <div className="flex justify-center items-center gap-4 mt-2 flex-wrap">
          <span className="text-sm text-orange-500 font-medium">🔮 You (Astrologer)</span>
          <span className="text-sm text-gray-400">↔</span>
          <span className="text-sm text-blue-600 font-medium">👤 {userName || 'User'}</span>
          
          {/* Connection Status */}
          {connectionState === 'connecting' && (
            <div className="flex items-center gap-2 ml-2 px-3 py-1 bg-yellow-100 rounded-full">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-yellow-700 font-medium">Connecting...</span>
            </div>
          )}
          {connectionState === 'connected' && !isLocalReconnecting && (
            <div className="flex items-center gap-2 ml-2 px-3 py-1 bg-green-100 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-xs text-green-700 font-medium">Connected</span>
            </div>
          )}
          {isLocalReconnecting && (
            <div className="flex items-center gap-2 ml-2 px-3 py-1 bg-yellow-100 rounded-full">
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-yellow-700 font-medium">Reconnecting…</span>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel - Main call area */}
        <div className="flex-1 p-4 lg:p-6">
          <div className="bg-gray-100 rounded-xl border border-orange-500 h-full flex flex-col relative">
            {/* User info + timer */}
            <div className="p-4 lg:p-6 flex justify-between items-start">
              <div>
                <h2 className="text-lg lg:text-xl font-semibold text-gray-900">{userName || 'User'}</h2>
                <SessionTimer seconds={callDuration} isConnected={connectionState === 'connected'} />
              </div>
              
              {/* Live earnings — real net after commission, straight from
                  the billing engine. Partner view doesn't distinguish trial
                  vs paid calls: they earn the same rate either way, and
                  knowing "this is a trial" can subtly change effort. */}
              <div className="flex items-center gap-2 bg-green-100 text-green-700 py-2 px-4 rounded-full">
                <IndianRupee className="w-4 h-4" />
                <span className="font-medium text-sm">
                  ₹{fmtMoney(liveBilling?.partnerNetEarning)}
                </span>
              </div>
            </div>

            {/* Main video/avatar area */}
            <div className="flex-1 flex items-center justify-center pb-28">
              <div className="relative">
                {/* Remote video or avatar */}
                {mode === 'video' && remoteUsers.length > 0 && remoteUsers[0].videoTrack ? (
                  <div
                    ref={el => remoteVideoRefs.current[remoteUsers[0].uid] = el}
                    className="w-64 h-64 lg:w-80 lg:h-80 rounded-xl overflow-hidden border-4 border-orange-200 shadow-lg bg-gray-800"
                  />
                ) : (
                  <div className="relative">
                    <UserAvatar name={userName} imageUrl={userPicture} size="lg" />
                    <div className={`absolute bottom-1 right-1 w-5 h-5 rounded-full border-2 border-white shadow ${
                      remoteUsers.length > 0 ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                  </div>
                )}
              </div>

              {/* Local video PiP for video calls */}
              {mode === 'video' && videoEnabled && (
                <div
                  ref={localVideoRef}
                  className="absolute bottom-32 right-4 lg:right-8 w-28 h-20 lg:w-36 lg:h-24 rounded-lg overflow-hidden border-2 border-blue-300 bg-gray-800 shadow-lg"
                >
                  <span className="absolute bottom-0 left-0 right-0 text-center text-xs bg-black/50 text-white py-0.5">
                    You
                  </span>
                </div>
              )}
            </div>

            {/* Call controls */}
            <div className="absolute bottom-6 left-0 right-0 flex justify-center">
              <div className="flex items-center gap-3 lg:gap-4">
                {/* Camera Toggle (video calls only) */}
                {mode === 'video' && (
                  <div className="flex flex-col items-center">
                    <button
                      onClick={toggleVideo}
                      className={`w-14 h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center shadow-md transition-all hover:scale-105 ${
                        videoEnabled ? 'bg-purple-100 text-purple-600' : 'bg-red-100 text-red-600'
                      }`}
                    >
                      {videoEnabled ? <Video size={22} /> : <VideoOff size={22} />}
                    </button>
                    <span className="text-xs mt-1 text-gray-600 font-medium">{videoEnabled ? 'Camera' : 'Off'}</span>
                  </div>
                )}
                
                {/* Microphone Toggle */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={toggleAudio}
                    className={`w-14 h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center shadow-md transition-all hover:scale-105 ${
                      audioEnabled ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {audioEnabled ? <Mic size={22} /> : <MicOff size={22} />}
                  </button>
                  <span className="text-xs mt-1 text-gray-600 font-medium">{audioEnabled ? 'Mute' : 'Unmuted'}</span>
                </div>
                
                {/* Speaker Toggle */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={toggleSpeaker}
                    className={`w-14 h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center shadow-md transition-all hover:scale-105 ${
                      speakerEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {speakerEnabled ? <Volume2 size={22} /> : <VolumeX size={22} />}
                  </button>
                  <span className="text-xs mt-1 text-gray-600 font-medium">{speakerEnabled ? 'Speaker' : 'Muted'}</span>
                </div>
                
                {/* End Call Button */}
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setShowConfirmEnd(true)}
                    className="w-14 h-14 lg:w-16 lg:h-16 rounded-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white shadow-md transition-all hover:scale-105"
                  >
                    <PhoneOff size={24} />
                  </button>
                  <span className="text-xs mt-1 text-gray-600 font-medium">Leave</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel - Sidebar (Desktop only) */}
        <div className="hidden lg:flex w-80 bg-white border-l border-gray-200 flex-col overflow-auto">
          {/* User Info section */}
          <SidebarPanel
            title="User Information"
            isExpanded={userInfoExpanded}
            onToggle={() => setUserInfoExpanded(!userInfoExpanded)}
          >
            <div className="flex items-center gap-3 mb-3">
              <UserAvatar name={userName} imageUrl={userPicture} size="sm" />
              <div>
                <p className="font-medium text-gray-800">{userName || 'User'}</p>
                <p className="text-sm text-gray-500">Consulting now</p>
              </div>
            </div>
            {userDetails ? (
              <div className="space-y-2 text-sm">
                {userDetails.dateOfBirth && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date of Birth</span>
                    <span className="text-gray-800 font-medium">{userDetails.dateOfBirth}</span>
                  </div>
                )}
                {userDetails.timeOfBirth && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Time of Birth</span>
                    <span className="text-gray-800 font-medium">{userDetails.timeOfBirth}</span>
                  </div>
                )}
                {userDetails.placeOfBirth && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Place of Birth</span>
                    <span className="text-gray-800 font-medium">{userDetails.placeOfBirth}</span>
                  </div>
                )}
                {userDetails.concern && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-gray-500 mb-1">Concern</p>
                    <p className="text-gray-800">{userDetails.concern}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600 text-sm">User details will appear here once submitted.</p>
            )}
          </SidebarPanel>

          {/* Chat section */}
          <SidebarPanel
            title="Chat"
            isExpanded={chatExpanded}
            onToggle={() => setChatExpanded(!chatExpanded)}
          >
            {userId ? (
              <InCallChat
                recipientId={`user_${userId}`}
                recipientName={userName}
              />
            ) : (
              <p className="text-gray-600 text-sm">Chat unavailable</p>
            )}
          </SidebarPanel>
          
          {/* Session Info — live data from billing engine */}
          <div className="mt-auto p-4 border-t border-gray-200">
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Session Duration</span>
                <span className="text-lg font-bold text-gray-800">
                  {Math.floor(callDuration / 60)}:{(callDuration % 60).toString().padStart(2, '0')}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Gross</span>
                <span>₹{fmtMoney(liveBilling?.partnerGrossEarning)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Commission ({liveBilling?.commissionPercent ?? 30}%)</span>
                <span>− ₹{fmtMoney(liveBilling?.partnerCommissionAmount)}</span>
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-gray-200">
                <span className="text-sm text-gray-700 font-medium">Your Net Earning</span>
                <span className="text-lg font-bold text-green-600">
                  ₹{fmtMoney(liveBilling?.partnerNetEarning)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reconnect overlay — shown when Agora reports the user dropped via
          ServerTimeOut. Stays up for RECONNECT_WINDOW_SECONDS, dismissed
          automatically when Agora fires user-joined. The astrologer can still
          manually end the call via the "End Now" button below if they don't
          want to wait. */}
      {isWaitingForReconnect && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full border-4 border-orange-300 border-t-orange-600 animate-spin" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Reconnecting…</h3>
            <p className="text-sm text-gray-500 mt-2">
              {userName || 'User'}'s network is unstable. Holding the call for{' '}
              <span className="font-semibold text-gray-800">{reconnectSecondsLeft}s</span>…
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Billing is paused while we wait.
            </p>
            <button
              onClick={() => cleanup('user_disconnected')}
              className="mt-5 w-full py-2.5 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              End Now
            </button>
          </div>
        </div>
      )}

      {/* End call confirmation modal */}
      {showConfirmEnd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Phone className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">End Consultation?</h3>
              <p className="text-gray-500 mt-1">Are you sure you want to end this call?</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmEnd(false)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleEndCall}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition"
              >
                End Call
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallPage;
